<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Rain streaks -->
      <div class="rain-layer" aria-hidden="true">
        <div class="rain-streak" v-for="n in 20" :key="n" :style="`left: ${(n-1)*5.2}%; animation-delay: ${(n * 0.17) % 2}s; animation-duration: ${0.6 + (n % 4) * 0.15}s`"></div>
      </div>

      <!-- Spotlight glow -->
      <div class="spotlight" aria-hidden="true"></div>

      <!-- Header — like a detective agency sign -->
      <header class="noir-header">
        <div class="nh-badge">
          <div class="badge-star">★</div>
          <div class="badge-text">
            <div class="bt-top">LICENSED</div>
            <div class="bt-main">LENDING</div>
          </div>
        </div>
        <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="nh-logo" />
      
      </header>

      <!-- Hero with big type -->
      <section class="noir-hero">
        <div class="nh-content">
          <div class="nh-byline">
            <span class="nb-rule"></span>
            <span class="nb-text">Case File: Home Loan</span>
            <span class="nb-rule"></span>
          </div>
          <h1 class="noir-h1" v-html="heroH1Html"></h1>
          <p class="noir-p">{{ heroBody }}</p>
          <div class="noir-btns">
            <a class="noir-btn" :href="applyRightLink">{{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="noir-btn-ghost" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
          <div class="noir-contacts">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="noir-contact">☎ {{ loinfo.phone_number }}</a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="noir-contact">✉ {{ loinfo.email }}</a>
          </div>
        </div>
      </section>

      <!-- Dossier cards -->
      <section class="noir-dossier">
        <div class="nd-label">— AGENT DOSSIER —</div>
        <div class="dossier-cards">

          <div class="dossier-card lo-dos">
            <div class="dc-stamp lo-stamp">LOAN OFFICER</div>
            <div class="dc-paperclip"></div>
            <div class="dc-photo-row">
              <div class="dc-photo-frame lo-photo-frame">
                <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="dc-photo" />
              </div>
              <div class="dc-details">
                <div class="dc-name">{{ loName }}</div>
                <div class="dc-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="dc-nmls">NMLS # {{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="dc-rule lo-rule"></div>
            <div class="dc-contacts">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="dc-link lo-link">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="dc-link lo-link">{{ loinfo.email }}</a>
            </div>
          
          </div>

          <div class="dossier-card re-dos">
            <div class="dc-stamp re-stamp">PARTNER</div>
            <div class="dc-photo-row">
              <div class="dc-photo-frame re-photo-frame">
                <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="dc-photo" />
              </div>
              <div class="dc-details">
                <div class="dc-name">{{ realtorName }}</div>
                <div class="dc-role">{{ realtor && realtor.brokerage_name }}</div>
                <div v-if="realtor && realtor.nmls" class="dc-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="dc-rule re-rule"></div>
            <div class="dc-contacts">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="dc-link re-link">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="dc-link re-link">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="dc-link re-link" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>

        </div>
      </section>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  return email.slice(email.indexOf("@") + 1).toLowerCase().split(".")[0] || null;
}
export default {
  name: "CoBrandNoir",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Every deal has<br/><em>a story.</em>";
      const parts = h.split(",");
      if (parts.length > 1) return `${parts[0].trim()}<br/><em>${parts.slice(1).join(",").trim()}</em>`;
      return h;
    },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      return [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim()
        || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Special+Elite&family=Libre+Franklin:ital,wght@0,300;0,400;0,700;1,300&display=swap');

.page {
  min-height: 100vh;
  background: #0c0c0a;
  color: #d4cfc4;
  font-family: 'Libre Franklin', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --amber: #c8860a;
  --smoke: rgba(200,195,180,0.06);
  --border: rgba(200,195,180,0.1);
  --muted: #8a8478;
  overflow: hidden;
  position: relative;
}
.root { position: relative; z-index: 1; display: flex; flex-direction: column; min-height: 100vh; }

/* RAIN */
.rain-layer { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
.rain-streak { position: absolute; top: -60px; width: 1px; height: 60px; background: linear-gradient(to bottom, transparent, rgba(200,195,180,0.12)); animation: rain linear infinite; }
@keyframes rain { from { transform: translateY(-60px); } to { transform: translateY(105vh); } }

/* SPOTLIGHT */
.spotlight { position: fixed; top: -200px; left: 30%; width: 500px; height: 700px; background: radial-gradient(ellipse at top, rgba(200,134,10,0.12) 0%, transparent 70%); pointer-events: none; z-index: 0; transform: rotate(10deg); }

/* HEADER */
.noir-header { position: relative; z-index: 10; display: flex; align-items: center; justify-content: space-between; padding: 18px 52px; border-bottom: 1px solid var(--border); background: rgba(12,12,10,0.95); }
.nh-badge { display: flex; align-items: center; gap: 10px; }
.badge-star { font-size: 20px; color: var(--amber); text-shadow: 0 0 12px rgba(200,134,10,0.5); }
.badge-text { display: flex; flex-direction: column; }
.bt-top { font-family: 'Special Elite', serif; font-size: 7px; letter-spacing: 0.3em; color: var(--muted); }
.bt-main { font-family: 'Special Elite', serif; font-size: 13px; letter-spacing: 0.18em; color: var(--amber); }
.nh-logo { width: 140px; max-height: 72px; object-fit: contain; filter: brightness(0) invert(0.65) sepia(0.4) hue-rotate(10deg); }
.nh-cta { font-family: 'Special Elite', serif; font-size: 11px; letter-spacing: 0.12em; text-decoration: none; background: var(--amber); color: #0c0c0a; padding: 10px 22px; border-radius: 2px; transition: opacity 0.2s; }
.nh-cta:hover { opacity: 0.88; }

/* HERO */
.noir-hero { position: relative; z-index: 1; padding: 72px 52px 52px; margin:auto }
.nh-content { max-width: 640px; }
.nh-byline { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; }
.nb-rule { flex: 0 0 30px; height: 1px; background: var(--amber); opacity: 0.5; }
.nb-text { font-family: 'Special Elite', serif; font-size: 10px; letter-spacing: 0.2em; color: var(--amber); opacity: 0.7; white-space: nowrap; }
.noir-h1 { font-family: 'Special Elite', serif; font-size: clamp(38px, 5.5vw, 70px); line-height: 1.06; color: #e8e4d8; margin: 0 0 22px; text-shadow: 2px 2px 0 rgba(0,0,0,0.6); }
.noir-h1 em { font-style: italic; color: var(--amber); }
.noir-p { font-size: 14px; line-height: 1.82; color: var(--muted); margin-bottom: 32px; font-weight: 300; font-style: italic; max-width: 480px;margin:auto }
.noir-btns { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px;justify-content: center; }
.noir-btn { justify-content: center; font-family: 'Special Elite', serif; font-size: 12px; letter-spacing: 0.1em; text-decoration: none; background: var(--amber); color: #0c0c0a; padding: 13px 28px; border-radius: 2px; transition: opacity 0.2s; width: 55%; text-align: center; }
.noir-btn:hover { opacity: 0.88; }
.noir-btn-ghost { font-family: 'Special Elite', serif; font-size: 12px; text-decoration: none; color: var(--muted); border: 1px solid var(--border); padding: 12px 22px; border-radius: 2px; transition: border-color 0.18s; }
.noir-btn-ghost:hover { border-color: var(--amber); color: var(--amber); }
.noir-contacts { display: flex; flex-direction: column; gap: 6px; }
.noir-contact { font-family: 'Special Elite', serif; font-size: 11px; text-decoration: none; color: var(--muted); transition: color 0.18s; letter-spacing: 0.06em; }
.noir-contact:hover { color: var(--amber); }

/* DOSSIER */
.noir-dossier { position: relative; z-index: 1; padding: 40px 52px 60px; background: rgba(0,0,0,0.3); border-top: 1px solid var(--border); }
.nd-label { font-family: 'Special Elite', serif; font-size: 10px; letter-spacing: 0.3em; color: var(--amber); opacity: 0.6; text-align: center; margin-bottom: 28px; }
.dossier-cards { display: flex; gap: 24px; max-width: 880px; margin: 0 auto; flex-wrap: wrap; justify-content: center; }

.dossier-card {
  flex: 0 0 360px;
  background: #141410;
  border: 1px solid var(--border);
  border-radius: 3px;
  padding: 24px;
  position: relative;
  overflow: hidden;
}
.lo-dos { box-shadow: -4px 0 0 var(--amber); }
.re-dos { box-shadow: -4px 0 0 color-mix(in srgb, var(--a) 60%, transparent); }
.dc-stamp {
  position: absolute; top: 16px; right: 16px;
  font-family: 'Special Elite', serif; font-size: 9px; letter-spacing: 0.2em;
  border: 2px solid; padding: 3px 8px; transform: rotate(5deg); opacity: 0.5;
}
.lo-stamp { color: var(--amber); border-color: var(--amber); }
.re-stamp { color: var(--a); border-color: var(--a); opacity: 0.5; }
.dc-paperclip { position: absolute; top: -3px; left: 30px; width: 16px; height: 28px; border: 3px solid #4a4840; border-radius: 8px 8px 0 0; }
.dc-photo-row { display: flex; gap: 16px; align-items: flex-start; margin-bottom: 16px; margin-top: 8px; }
.dc-photo-frame { flex-shrink: 0; padding: 3px; background: #2a2820; border: 1px solid #3a3830; }
.lo-photo-frame { border-color: rgba(200,134,10,0.3); }
.re-photo-frame { border-color: rgba(198,156,57,0.3); }
.dc-photo { width: 56px; height: 56px; object-fit: cover; display: block; filter: grayscale(30%) contrast(1.1); }
.dc-name { font-family: 'Special Elite', serif; font-size: 19px; color: #e8e4d8; line-height: 1.2; margin-bottom: 2px; }
.dc-role { font-size: 11px; color: var(--muted); font-style: italic; }
.dc-nmls { font-size: 10px; color: rgba(138,132,120,0.5); margin-top: 3px; }
.dc-rule { height: 1px; margin: 14px 0; }
.lo-rule { background: linear-gradient(90deg, var(--amber), transparent); }
.re-rule { background: linear-gradient(90deg, var(--a), transparent); }
.dc-contacts { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
.dc-link { font-family: 'Special Elite', serif; font-size: 11px; text-decoration: none; color: var(--muted); transition: color 0.18s; letter-spacing: 0.04em; }
.lo-link:hover { color: var(--amber); }
.re-link:hover { color: var(--a); }
.dc-cta { display: block; text-align: center; text-decoration: none; font-family: 'Special Elite', serif; font-size: 12px; letter-spacing: 0.1em; padding: 11px; border-radius: 2px; background: var(--amber); color: #0c0c0a; transition: opacity 0.2s; }
.dc-cta:hover { opacity: 0.88; }

@media (max-width: 820px) {
  .noir-header { padding: 14px 24px; }
  .nh-badge { display: none; }
  .noir-hero { padding: 48px 24px 36px; }
  .noir-dossier { padding: 32px 24px 48px; }
  .dossier-card { flex: 0 0 100%; }
}
</style>
